/**
 * Suggest a Product — user-facing form.
 *
 * Route : /food-pulse/suggest-product?gtin=…&name=…
 * Flow  : name + alt names + brand + gtin + category + photos +
 *         geographic origin (Country → Region dropdowns) + description.
 *         Backend checks fp_products_master + food_items for a match;
 *         if none, saves to fp_product_suggestions with AI enrichment
 *         and notifies admins.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Plus, X, Upload, MapPin, Send, CheckCircle2, Info } from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const GREEN = "#0F3D2E";
const CREAM = "#FBF7EF";
const CATS = ["produce", "dairy", "meat", "seafood", "bakery", "frozen", "beverage", "snack", "pantry", "other"];
const PERI = ["highly_perishable", "short_shelf", "shelf_stable", "non_perishable"];

export default function SuggestProduct() {
  const nav = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [params] = useSearchParams();

  const [form, setForm] = useState({
    name: params.get("name") || "",
    alternate_names: [],
    brand: "",
    gtin: params.get("gtin") || "",
    category: "",
    perishability_class: "",
    shelf_life_days: "",
    description: "",
    notes: "",
  });
  const [altInput, setAltInput] = useState("");
  const [countries, setCountries] = useState([]);
  const [regions, setRegions] = useState([]);
  const [geo, setGeo] = useState({ country: "", region: "", city: "" });
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    http.get("/geo/countries").then((r) => setCountries(r.data.countries || [])).catch(() => setCountries([]));
  }, []);
  useEffect(() => {
    setRegions([]); setGeo((g) => ({ ...g, region: "" }));
    if (!geo.country) return;
    const c = countries.find((x) => x.name === geo.country || x.code === geo.country);
    if (!c) return;
    http.get("/geo/subdivisions", { params: { country: c.code } })
      .then((r) => setRegions(r.data.subdivisions || r.data.items || []))
      .catch(() => setRegions([]));
  }, [geo.country, countries]);

  const addAlt = () => {
    const v = altInput.trim();
    if (!v) return;
    setForm({ ...form, alternate_names: [...form.alternate_names, v] });
    setAltInput("");
  };
  const removeAlt = (i) => setForm({ ...form, alternate_names: form.alternate_names.filter((_, x) => x !== i) });

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { alert("Please choose a photo smaller than 2 MB."); return; }
    setUploading(true);
    try {
      // Convert to a data URL — small photos travel directly with the suggestion.
      // Admins can move photos to Object Storage on approve.
      const dataUrl = await new Promise((res, rej) => {
        const fr = new FileReader();
        fr.onload = () => res(fr.result);
        fr.onerror = rej;
        fr.readAsDataURL(file);
      });
      setPhotos((p) => [...p, dataUrl]);
    } catch (err) { /* noop */ }
    setUploading(false);
    e.target.value = "";
  };

  const submit = async () => {
    setBusy(true); setError(""); setResult(null);
    try {
      const body = {
        name: form.name,
        alternate_names: form.alternate_names,
        brand: form.brand || undefined,
        gtin: form.gtin || undefined,
        category: form.category || undefined,
        perishability_class: form.perishability_class || undefined,
        shelf_life_days: form.shelf_life_days ? Number(form.shelf_life_days) : undefined,
        description: form.description || undefined,
        photos,
        geographic_origin: (geo.country || geo.region || geo.city) ? geo : undefined,
        notes: form.notes || undefined,
      };
      const r = await http.post("/inventory/product-suggestions", body);
      setResult(r.data);
    } catch (e) { setError(e.response?.data?.detail || "Submit failed"); }
    setBusy(false);
  };

  if (authLoading) return <div className="p-10 text-center"><Loader2 className="animate-spin mx-auto" /></div>;
  if (!user) return <div className="p-10 text-center">Please sign in to suggest a product.</div>;

  if (result) {
    return (
      <main className="min-h-screen py-12" style={{ background: CREAM }} data-testid="suggest-product-result">
        <div className="max-w-lg mx-auto bg-white rounded-2xl border p-6 shadow-sm text-center" style={{ borderColor: "#E9D8B4" }}>
          <CheckCircle2 size={48} className="mx-auto mb-3" style={{ color: GREEN }} />
          {result.matched ? (
            <>
              <h2 className="text-xl font-black" style={{ color: GREEN }}>Already in FoodPulse!</h2>
              <p className="text-sm text-gray-700 mt-2">We found <b>{result.product.name}</b> in our catalog. You can start tracking it right away.</p>
            </>
          ) : (
            <>
              <h2 className="text-xl font-black" style={{ color: GREEN }}>Thanks for the suggestion!</h2>
              <p className="text-sm text-gray-700 mt-2">
                Your submission for <b>{result.suggestion?.name}</b> is queued for our team to review. You'll get a notification when it's live.
              </p>
              {result.suggestion?.ai_enrichment && (
                <div className="mt-3 rounded-xl p-3 text-left text-xs" style={{ background: "#F7F2E4", color: GREEN }}>
                  <div className="font-semibold flex items-center gap-1"><Info size={12} /> AI pre-classification</div>
                  <div>Canonical: {result.suggestion.ai_enrichment.canonical_name || "—"}</div>
                  <div>Category: {result.suggestion.ai_enrichment.category || "—"}</div>
                  <div>Perishability: {result.suggestion.ai_enrichment.perishability_class || "—"}</div>
                </div>
              )}
            </>
          )}
          <button onClick={() => nav("/food-pulse")} className="mt-5 rounded-full px-4 py-2 text-sm font-semibold" style={{ background: GREEN, color: CREAM }} data-testid="back-home-btn">Back to FoodPulse</button>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen py-8" style={{ background: CREAM }} data-testid="suggest-product-ai-page">
      <div className="max-w-3xl mx-auto px-4">
        <h1 className="text-2xl font-black" style={{ color: GREEN }}>Suggest a product</h1>
        <p className="text-sm text-gray-600 mb-6">Know a product we don't yet track? Tell us about it — we'll check our database and add it if it's new. AI helps pre-classify while our team reviews.</p>

        <div className="rounded-2xl bg-white border p-5 grid gap-4" style={{ borderColor: "#E9D8B4" }}>
          <div className="grid md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <label className="text-xs font-semibold text-gray-600">Common name <span className="text-red-600">*</span></label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Kokum" className="w-full border rounded-lg px-3 py-2 text-sm mt-1" style={{ borderColor: "#E9D8B4" }} data-testid="sp-name-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600">Brand (optional)</label>
              <input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm mt-1" style={{ borderColor: "#E9D8B4" }} data-testid="sp-brand-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600">GTIN / barcode (optional)</label>
              <input value={form.gtin} onChange={(e) => setForm({ ...form, gtin: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm mt-1" style={{ borderColor: "#E9D8B4" }} data-testid="sp-gtin-input" />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600">Alternate names (regional, scientific, colloquial)</label>
            <div className="flex gap-2 mt-1">
              <input value={altInput} onChange={(e) => setAltInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addAlt())} placeholder="e.g. Garcinia indica" className="flex-1 border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} data-testid="sp-alt-input" />
              <button onClick={addAlt} className="rounded-lg px-3 py-2 text-sm font-semibold" style={{ background: GREEN, color: CREAM }} data-testid="sp-add-alt-btn"><Plus size={14} /></button>
            </div>
            <div className="flex flex-wrap gap-1 mt-2">
              {form.alternate_names.map((n, i) => (
                <span key={i} className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs" style={{ background: "#E9D8B4", color: GREEN }}>{n}<button onClick={() => removeAlt(i)}><X size={10} /></button></span>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600">Category</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm mt-1" style={{ borderColor: "#E9D8B4" }} data-testid="sp-category">
                <option value="">—</option>
                {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600">Perishability</label>
              <select value={form.perishability_class} onChange={(e) => setForm({ ...form, perishability_class: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm mt-1" style={{ borderColor: "#E9D8B4" }} data-testid="sp-peri">
                <option value="">—</option>
                {PERI.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600">Shelf life (days)</label>
              <input type="number" min="0" value={form.shelf_life_days} onChange={(e) => setForm({ ...form, shelf_life_days: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm mt-1" style={{ borderColor: "#E9D8B4" }} data-testid="sp-shelf" />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600 flex items-center gap-1"><MapPin size={12} /> Geographic origin (optional)</label>
            <div className="grid md:grid-cols-3 gap-2 mt-1">
              <select value={geo.country} onChange={(e) => setGeo({ ...geo, country: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} data-testid="sp-geo-country">
                <option value="">Country…</option>
                {countries.map((c) => <option key={c.code} value={c.name}>{c.flag} {c.name}</option>)}
              </select>
              <select value={geo.region} onChange={(e) => setGeo({ ...geo, region: e.target.value })} disabled={!regions.length} className="border rounded-lg px-3 py-2 text-sm disabled:opacity-50" style={{ borderColor: "#E9D8B4" }} data-testid="sp-geo-region">
                <option value="">Region…</option>
                {regions.map((s) => <option key={s.code || s.name} value={s.name}>{s.name}</option>)}
              </select>
              <input value={geo.city} onChange={(e) => setGeo({ ...geo, city: e.target.value })} placeholder="City / origin place" className="border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} data-testid="sp-geo-city" />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600">Description</label>
            <textarea rows="3" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What it is, how it's used, unique traits…" className="w-full border rounded-lg px-3 py-2 text-sm mt-1" style={{ borderColor: "#E9D8B4" }} data-testid="sp-description" />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600">Photos (up to 8)</label>
            <div className="flex flex-wrap gap-2 mt-1">
              {photos.map((p, i) => (
                <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border" style={{ borderColor: "#E9D8B4" }}>
                  <img src={p} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => setPhotos(photos.filter((_, x) => x !== i))} className="absolute top-0 right-0 bg-red-600 text-white rounded-bl-lg p-0.5"><X size={10} /></button>
                </div>
              ))}
              {photos.length < 8 && (
                <label className="w-20 h-20 rounded-lg border-2 border-dashed flex items-center justify-center cursor-pointer" style={{ borderColor: "#E9D8B4", color: GREEN }} data-testid="sp-photo-upload">
                  {uploading ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />}
                  <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} disabled={uploading} />
                </label>
              )}
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600">Notes for the FoodPulse team</label>
            <textarea rows="2" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm mt-1" style={{ borderColor: "#E9D8B4" }} data-testid="sp-notes" />
          </div>

          {error && <div className="text-sm text-red-700" data-testid="sp-error">{error}</div>}
          <button onClick={submit} disabled={busy || !form.name.trim()} className="rounded-full px-4 py-3 font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: GREEN, color: CREAM }} data-testid="sp-submit-btn">
            {busy ? <Loader2 className="animate-spin" size={16} /> : <Send size={14} />} Submit suggestion
          </button>
        </div>
      </div>
    </main>
  );
}
